import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import LoginIcon from '@mui/icons-material/Login';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import salesService, { ValidateCashierPinDto, ValidateCashierResponseDto } from '../../../../../services/salesService';

// ✅ Kasiyer Yetkileri Tipi
export interface CashierPermissions {
  // Normal Hızlı Satış Yetkileri
  canChangePrice: boolean;
  canApplyDiscount: boolean;
  canCancelSale: boolean;
  canViewReports: boolean;
  canViewEndOfDay: boolean;
  canWarehouseOperations: boolean;
  canViewAccountBalance: boolean;
  canListAccounts: boolean;
  canQuickAddItem: boolean;
  canAddAccount: boolean;
  // Restoran Yetkileri
  canCloseSale: boolean;
  canCloseTable: boolean;
  canAddProduct: boolean;
  canRemoveProduct: boolean;
  canClearCart: boolean;
  canPrintToKitchen: boolean;
  canDeletePrintedItems: boolean;
  canDeleteUnprintedItems: boolean; // Yazdırılmamış ürün silme yetkisi
  canTransferTable: boolean;
  canPosRefund: boolean;
  canCancelReceipt: boolean; // Adisyon iptal yetkisi
  canCancelUnprintedReceipt: boolean; // Yazdırılmamış adisyon iptal yetkisi
  canPrintReceipt: boolean; // Adisyon yazdırma yetkisi
  canAddProductAfterReceiptPrint: boolean; // Adisyon sonrası ürün ekleme
}

// ✅ Kasiyer Bilgisi Tipi
export interface CashierInfo {
  cashierId: number;
  cashierName: string;
  permissions: CashierPermissions;
}

interface CashierLoginModalProps {
  open: boolean;
  screenType: 'QuickSale' | 'Restaurant';
  quickSaleScreenId?: number | null;
  restaurantScreenId?: number | null;
  onLogin: (cashierInfo: CashierInfo) => void;
  onClose?: () => void; // Opsiyonel - eğer varsa kapatma butonu gösterilir
  allowClose?: boolean; // Kapatma butonu gösterilsin mi? (varsayılan: true)
  title?: string; // Özel başlık
}

const CashierLoginModal: React.FC<CashierLoginModalProps> = ({
  open,
  screenType,
  quickSaleScreenId,
  restaurantScreenId,
  onLogin,
  onClose,
  allowClose = true,
  title = 'Kasiyer Girişi',
}) => {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal açıldığında PIN'i temizle
  useEffect(() => {
    if (open) {
      setPin('');
      setError(null);
    }
  }, [open]);

  // Numara butonu tıklama
  const handleNumberClick = (num: string) => {
    if (pin.length < 6) {
      setPin(pin + num);
      setError(null);
    }
  };

  // Backspace
  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
    setError(null);
  };

  // Clear (Temizle)
  const handleClear = () => {
    setPin('');
    setError(null);
  };

  // Enter - Şifre doğrulama
  const handleEnter = async () => {
    if (!pin) {
      setError('Lütfen şifrenizi girin');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const requestData: ValidateCashierPinDto = {
        ScreenType: screenType,
        QuickSaleScreenId: screenType === 'QuickSale' ? quickSaleScreenId : null,
        RestaurantScreenId: screenType === 'Restaurant' ? restaurantScreenId : null,
        Pin: pin,
      };

      const response: ValidateCashierResponseDto = await salesService.validateCashierPin(requestData);

      if (response.IsValid && response.CashierId && response.CashierName) {
        // ✅ TÜM YETKİLERİ API'DEN AL
        const cashierInfo: CashierInfo = {
          cashierId: response.CashierId,
          cashierName: response.CashierName,
          permissions: {
            // Normal Hızlı Satış Yetkileri
            canChangePrice: response.CanChangePrice ?? false,
            canApplyDiscount: response.CanApplyDiscount ?? false,
            canCancelSale: response.CanCancelSale ?? false,
            canViewReports: response.CanViewReports ?? false,
            canViewEndOfDay: response.CanViewEndOfDay ?? false,
            canWarehouseOperations: response.CanWarehouseOperations ?? false,
            canViewAccountBalance: response.CanViewAccountBalance ?? false,
            canListAccounts: response.CanListAccounts ?? false,
            canQuickAddItem: response.CanQuickAddItem ?? false,
            canAddAccount: response.CanAddAccount ?? false,
            // Restoran Yetkileri
            canCloseSale: response.CanCloseSale ?? false,
            canCloseTable: response.CanCloseTable ?? false,
            canAddProduct: response.CanAddProduct ?? false,
            canRemoveProduct: response.CanRemoveProduct ?? false,
            canClearCart: response.CanClearCart ?? false,
            canPrintToKitchen: response.CanPrintToKitchen ?? false,
            canDeletePrintedItems: response.CanDeletePrintedItems ?? false,
            canDeleteUnprintedItems: response.CanDeleteUnprintedItems ?? false, // NULL ise kapalı
            canTransferTable: response.CanTransferTable ?? false,
            canPosRefund: response.CanPosRefund ?? false,
            canCancelReceipt: response.CanCancelReceipt ?? false, // NULL ise kapalı
            canCancelUnprintedReceipt: response.CanCancelUnprintedReceipt ?? false, // NULL ise kapalı
            canPrintReceipt: response.CanPrintReceipt ?? false, // NULL ise kapalı
            canAddProductAfterReceiptPrint: response.CanAddProductAfterReceiptPrint ?? false, // NULL ise kapalı
          },
        };

        console.log('✅ [CashierLoginModal] Kasiyer giriş yaptı:', cashierInfo.cashierName);
        onLogin(cashierInfo);
        setPin('');
      } else {
        setError(response.Message || 'Geçersiz şifre');
        setPin('');
      }
    } catch (err: any) {
      console.error('Şifre doğrulama hatası:', err);
      setError('Şifre doğrulama sırasında bir hata oluştu');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // Enter tuşu desteği
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEnter();
    } else if (e.key === 'Backspace') {
      handleBackspace();
    } else if (e.key === 'Escape' && allowClose && onClose) {
      onClose();
    } else if (e.key >= '0' && e.key <= '9') {
      handleNumberClick(e.key);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={allowClose ? onClose : undefined}
      disableEscapeKeyDown={!allowClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: '380px',
          p: 0,
          overflow: 'hidden',
        },
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonIcon sx={{ fontSize: '1.5rem' }} />
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
        </Box>
        {allowClose && onClose && (
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* PIN Input ve Numpad */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: 'background.paper',
        }}
        onKeyDown={handleKeyPress}
        tabIndex={0}
      >
        {/* PIN Input */}
        <TextField
          fullWidth
          type="password"
          value={pin}
          placeholder="••••••"
          InputProps={{
            readOnly: true,
            sx: {
              fontSize: '2rem',
              textAlign: 'center',
              letterSpacing: '0.5rem',
              fontWeight: 'bold',
            },
          }}
          sx={{
            mb: 2,
            '& input': {
              textAlign: 'center',
            },
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Numerik Keypad */}
        <Grid container spacing={1.5}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <Grid item xs={4} key={num}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleNumberClick(num.toString())}
                disabled={loading}
                sx={{
                  fontSize: '1.5rem',
                  height: 56,
                  fontWeight: 'bold',
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {num}
              </Button>
            </Grid>
          ))}

          {/* Alt satır: Clear, 0, Backspace */}
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleClear}
              disabled={loading}
              sx={{
                fontSize: '0.9rem',
                height: 56,
                fontWeight: 'bold',
                color: 'error.main',
                borderColor: 'error.main',
                '&:hover': { bgcolor: 'error.light', borderColor: 'error.dark' },
              }}
            >
              Temizle
            </Button>
          </Grid>

          <Grid item xs={4}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => handleNumberClick('0')}
              disabled={loading}
              sx={{
                fontSize: '1.5rem',
                height: 56,
                fontWeight: 'bold',
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              0
            </Button>
          </Grid>

          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleBackspace}
              disabled={loading}
              sx={{
                fontSize: '1.2rem',
                height: 56,
                fontWeight: 'bold',
              }}
            >
              <BackspaceIcon />
            </Button>
          </Grid>
        </Grid>

        {/* Giriş Butonu */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          color="success"
          onClick={handleEnter}
          disabled={loading || !pin}
          startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <LoginIcon />}
          sx={{
            mt: 2.5,
            height: 60,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            bgcolor: 'success.main',
            '&:hover': { bgcolor: 'success.dark' },
          }}
        >
          {loading ? 'Doğrulanıyor...' : 'GİRİŞ'}
        </Button>
      </Paper>
    </Dialog>
  );
};

export default CashierLoginModal;
